import React, { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { Package, Heart, X, FileText, Download, TrendingUp, Sparkles, ShoppingBag, HandHeart, ArrowRight } from "lucide-react";
import api, { API } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import VolunteerHoursSelfPanel from "../components/VolunteerHoursSelfPanel";
import OrganizerCard from "../components/OrganizerCard";
import AccountSettingsPanel from "../components/AccountSettingsPanel";
import SecurityKeysPanel from "../components/SecurityKeysPanel";

const STATUS_COLORS = {
    new: "bg-blue-100 text-blue-700",
    processing: "bg-amber-100 text-amber-700",
    shipped: "bg-purple-100 text-purple-700",
    delivered: "bg-green-100 text-green-700",
    cancelled: "bg-gray-100 text-gray-600",
};

// Section header used across the donor account so every block — Order history,
// Monthly giving, Volunteer hours, Organizer status, Security keys, Settings —
// gets the same red eyebrow + heading + thin divider treatment.
const AccountSection = ({ eyebrow, title, icon: Icon, children, testid }) => (
    <section className="mt-12 first:mt-0" data-testid={testid}>
        <div className="mb-5">
            {eyebrow && <p className="text-xs tracking-[0.18em] uppercase font-bold text-[#E53935] mb-2">{eyebrow}</p>}
            <div className="flex items-center gap-3">
                {Icon && <Icon className="w-5 h-5 text-[#E53935]" strokeWidth={1.5} />}
                <h2 className="font-heading text-2xl text-[#1C252E] font-medium tracking-tight">{title}</h2>
            </div>
        </div>
        {children}
    </section>
);

const Account = () => {
    const { user, loading } = useAuth();
    const [orders, setOrders] = useState([]);
    const [donations, setDonations] = useState([]);
    const [summary, setSummary] = useState(null);
    const [contributed, setContributed] = useState(null);
    const [fetching, setFetching] = useState(true);

    const loadDonations = () =>
        api.get("/my/donations").then((r) => setDonations(r.data)).catch(() => {});

    useEffect(() => {
        if (!user) return;
        Promise.all([
            api.get("/my/orders").then((r) => setOrders(r.data)).catch(() => {}),
            loadDonations(),
            api.get("/my/donations/summary").then((r) => setSummary(r.data)).catch(() => {}),
            api.get("/my/donations/contributed-projects").then((r) => setContributed(r.data)).catch(() => {}),
        ]).finally(() => setFetching(false));
    }, [user]);

    const cancelMonthly = async (id) => {
        if (!window.confirm("Cancel this recurring monthly donation?")) return;
        try {
            await api.put(`/donations/${id}/cancel`);
            toast.success("Monthly donation cancelled");
            loadDonations();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Cancel failed");
        }
    };

    if (loading) return <div className="p-20 text-center text-[#4A5568]">Loading…</div>;
    if (!user) return <Navigate to="/login" replace />;

    return (
        <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12 py-12 lg:py-16" data-testid="account-page">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-10 gap-4">
                <div>
                    <p className="text-xs tracking-[0.18em] uppercase font-bold text-[#E53935] mb-3">My account</p>
                    <h1 className="font-heading text-4xl text-[#1C252E] font-medium tracking-tight">
                        Welcome, {user.name?.split(" ")[0] || "friend"}
                    </h1>
                </div>
                <p className="text-sm text-[#4A5568]">
                    Signed in via <span className="capitalize font-medium text-[#1C252E]">{user.provider}</span> · {user.email}
                </p>
            </div>

            {/* Lifetime giving summary — warm card matching foundation aesthetic
                (replaces the old dark-gradient SaaS-style block) */}
            {summary && summary.lifetime_count > 0 && (
                <div className="mb-12 bg-white border border-black/5 rounded-3xl p-8 shadow-sm" data-testid="account-giving-summary">
                    <div className="flex items-center gap-2 mb-6">
                        <TrendingUp className="w-4 h-4 text-[#E53935]" strokeWidth={2} />
                        <p className="text-xs tracking-[0.18em] uppercase font-bold text-[#E53935]">Your lifetime giving</p>
                    </div>
                    <div className="grid sm:grid-cols-3 gap-8 mb-6 pb-6 border-b border-black/5">
                        <div>
                            <p className="text-[10px] uppercase tracking-wider font-bold text-[#4A5568]">Total contributed</p>
                            <p className="font-heading text-4xl text-[#1C252E] font-medium mt-1" data-testid="account-lifetime-total">
                                ${summary.lifetime_total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </p>
                        </div>
                        <div>
                            <p className="text-[10px] uppercase tracking-wider font-bold text-[#4A5568]">Total gifts</p>
                            <p className="font-heading text-4xl text-[#1C252E] font-medium mt-1" data-testid="account-lifetime-count">
                                {summary.lifetime_count}
                            </p>
                        </div>
                        <div>
                            <p className="text-[10px] uppercase tracking-wider font-bold text-[#4A5568]">Active recurring</p>
                            <p className="font-heading text-4xl text-[#1C252E] font-medium mt-1" data-testid="account-active-recurring">
                                {summary.active_recurring_count}
                            </p>
                        </div>
                    </div>
                    {summary.by_year && summary.by_year.length > 0 && (
                        <div className="flex flex-wrap items-center gap-2" data-testid="account-by-year">
                            <span className="text-[10px] uppercase tracking-wider font-bold text-[#4A5568] mr-2">Bulk receipts</span>
                            {summary.by_year.map((y) => (
                                <a
                                    key={y.year}
                                    href={`${API}/my/donations/receipts.zip?year=${y.year}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    data-testid={`account-receipts-zip-${y.year}`}
                                    className="inline-flex items-center gap-1.5 bg-[#F4F1EA] hover:bg-[#EBE5D8] text-[#1C252E] rounded-full px-3 py-1.5 text-xs font-medium transition-all border border-black/5"
                                    title={`Download ${y.count} receipts from ${y.year} as ZIP`}
                                >
                                    <Download className="w-3 h-3" strokeWidth={2} />
                                    {y.year} · ${y.total.toFixed(0)}
                                </a>
                            ))}
                            <a
                                href={`${API}/my/donations/receipts.zip`}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="account-receipts-zip-all"
                                className="inline-flex items-center gap-1.5 bg-[#E53935] hover:bg-[#D32F2F] text-white rounded-full px-3 py-1.5 text-xs font-medium transition-all ml-auto"
                            >
                                <Download className="w-3 h-3" strokeWidth={2} />
                                All years
                            </a>
                        </div>
                    )}
                    {contributed && contributed.count > 0 && (
                        <Link
                            to="/impact"
                            className="block mt-5 bg-[#F2D8D5] hover:bg-[#EBC8C4] rounded-2xl px-5 py-4 transition-all"
                            data-testid="account-contributed-projects-badge"
                        >
                            <p className="text-[10px] uppercase tracking-wider font-bold text-[#E53935] mb-1 flex items-center gap-1.5">
                                <Sparkles className="w-3.5 h-3.5" strokeWidth={2} />
                                Your impact so far
                            </p>
                            <p className="text-sm text-[#1C252E] leading-snug">
                                Since your first gift, your foundation has paid out{" "}
                                <strong className="text-[#E53935]">{contributed.count}</strong>{" "}
                                community {contributed.count === 1 ? "project" : "projects"}. See what your support helped build →
                            </p>
                        </Link>
                    )}
                </div>
            )}

            {/* Empty-state "Get started" trio — shown for users with no giving history yet. */}
            {summary && summary.lifetime_count === 0 && (
                <div className="mb-12 bg-white border border-[#E53935]/20 rounded-3xl p-8" data-testid="account-empty-state">
                    <div className="flex items-start gap-3 mb-6">
                        <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-[#F2D8D5] text-[#E53935] shrink-0">
                            <Sparkles className="w-5 h-5" strokeWidth={1.5} />
                        </span>
                        <div>
                            <p className="text-xs tracking-[0.18em] uppercase font-bold text-[#E53935] mb-1">Welcome aboard</p>
                            <h2 className="font-heading text-2xl text-[#1C252E] font-medium tracking-tight">Three ways to start making a dent</h2>
                            <p className="text-sm text-[#4A5568] mt-2 max-w-xl">No giving history yet — that's okay, everyone starts here. Pick something below and your account will start tracking your impact.</p>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <Link to="/donate" data-testid="account-cta-donate" className="group bg-[#FDFBF7] border border-black/5 rounded-2xl p-5 hover:border-[#E53935]/40 hover:shadow-md transition flex flex-col">
                            <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-[#E53935] text-white mb-3"><Heart className="w-4 h-4" strokeWidth={2} /></span>
                            <p className="font-heading text-lg text-[#1C252E] leading-snug">Make your first gift</p>
                            <p className="text-xs text-[#4A5568] mt-1 flex-1">One-time or monthly. Every dollar lands on the public ledger.</p>
                            <p className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-[#E53935] group-hover:gap-2 transition-all">Donate <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} /></p>
                        </Link>
                        <Link to="/shop" data-testid="account-cta-shop" className="group bg-[#FDFBF7] border border-black/5 rounded-2xl p-5 hover:border-[#E53935]/40 hover:shadow-md transition flex flex-col">
                            <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-[#1C252E] text-white mb-3"><ShoppingBag className="w-4 h-4" strokeWidth={2} /></span>
                            <p className="font-heading text-lg text-[#1C252E] leading-snug">Buy from the shop</p>
                            <p className="text-xs text-[#4A5568] mt-1 flex-1">Foundation merchandise. Proceeds fund programs directly.</p>
                            <p className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-[#E53935] group-hover:gap-2 transition-all">Shop <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} /></p>
                        </Link>
                        <Link to="/get-involved" data-testid="account-cta-volunteer" className="group bg-[#FDFBF7] border border-black/5 rounded-2xl p-5 hover:border-[#E53935]/40 hover:shadow-md transition flex flex-col">
                            <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-amber-500 text-white mb-3"><HandHeart className="w-4 h-4" strokeWidth={2} /></span>
                            <p className="font-heading text-lg text-[#1C252E] leading-snug">Volunteer your time</p>
                            <p className="text-xs text-[#4A5568] mt-1 flex-1">Events, drives, board roles — find a fit for your schedule.</p>
                            <p className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-[#E53935] group-hover:gap-2 transition-all">Get involved <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} /></p>
                        </Link>
                    </div>
                </div>
            )}

            <AccountSection eyebrow="Receipts &amp; fulfillment" title="Order history" icon={Package} testid="account-orders-section">
                {fetching ? (
                    <p className="text-[#4A5568]" data-testid="account-orders-loading">Loading orders…</p>
                ) : orders.length === 0 ? (
                    <div className="bg-white border border-black/5 rounded-2xl p-10 text-center" data-testid="account-orders-empty">
                        <p className="text-[#4A5568] mb-4">You haven't placed any orders yet.</p>
                        <Link to="/shop" className="inline-flex items-center gap-2 bg-[#E53935] text-white hover:bg-[#D32F2F] rounded-full px-6 py-3 font-medium transition-all">
                            Start shopping
                        </Link>
                    </div>
                ) : (
                    <div className="space-y-4" data-testid="account-orders-list">
                        {orders.map((o) => (
                            <div key={o.id} className="bg-white border border-black/5 rounded-2xl p-6" data-testid={`account-order-${o.id}`}>
                                <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                                    <div>
                                        <p className="text-[10px] uppercase tracking-wider font-bold text-[#4A5568]">Order #</p>
                                        <p className="font-mono text-sm text-[#1C252E] mt-0.5">{o.order_number}</p>
                                    </div>
                                    <div>
                                        <p className="text-[10px] uppercase tracking-wider font-bold text-[#4A5568]">Placed</p>
                                        <p className="text-sm text-[#1C252E] mt-0.5">{new Date(o.created_at).toLocaleDateString()}</p>
                                    </div>
                                    <div>
                                        <p className="text-[10px] uppercase tracking-wider font-bold text-[#4A5568]">Status</p>
                                        <span className={`inline-block mt-0.5 px-3 py-0.5 rounded-full text-xs font-bold capitalize ${STATUS_COLORS[o.fulfillment_status] || "bg-gray-100 text-gray-700"}`}>
                                            {o.fulfillment_status}
                                        </span>
                                    </div>
                                    <div>
                                        <p className="text-[10px] uppercase tracking-wider font-bold text-[#4A5568]">Total</p>
                                        <p className="font-heading text-lg font-medium text-[#1C252E] mt-0.5">${o.total.toFixed(2)}</p>
                                    </div>
                                </div>
                                {(o.payment_method || o.payment_reference) && (
                                    <div className="mb-3 -mt-1 flex flex-wrap items-center gap-2 text-xs" data-testid={`account-order-payment-${o.id}`}>
                                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full font-semibold uppercase tracking-wider text-[10px] ${o.payment_method === "stripe" ? "bg-indigo-100 text-indigo-700" : o.payment_method === "paypal" ? "bg-blue-100 text-blue-700" : "bg-slate-100 text-slate-600"}`}>
                                            {o.payment_method || "—"}
                                        </span>
                                        {o.payment_reference && (
                                            <span className="font-mono text-[10px] text-[#4A5568]" title={o.payment_reference}>
                                                Ref: {o.payment_reference.length > 24 ? `${o.payment_reference.slice(0, 12)}…${o.payment_reference.slice(-6)}` : o.payment_reference}
                                            </span>
                                        )}
                                    </div>
                                )}
                                <div className="flex gap-3 overflow-x-auto no-scrollbar border-t border-black/5 pt-4">
                                    {o.items.map((i) => (
                                        <div key={i.product_id} className="flex gap-3 items-center min-w-0 flex-shrink-0">
                                            <img src={i.image_url} alt="" className="w-12 h-12 rounded-lg object-cover bg-[#F4F1EA]" />
                                            <div className="min-w-0">
                                                <p className="text-sm font-medium text-[#1C252E] truncate max-w-[160px]">{i.name}</p>
                                                <p className="text-xs text-[#4A5568]">Qty {i.quantity}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                {o.assigned_slot_snapshot?.display && (
                                    <div
                                        data-testid={`account-order-pickup-slot-${o.id}`}
                                        className="mt-4 pt-4 border-t border-black/5 rounded-lg bg-blue-50/60 border border-blue-100 p-3"
                                    >
                                        <p className="text-[10px] uppercase tracking-wider font-bold text-blue-800 mb-0.5">Pickup window</p>
                                        <p className="text-sm font-semibold text-blue-900">{o.assigned_slot_snapshot.display}</p>
                                        {o.assigned_slot_snapshot.location && (
                                            <p className="text-xs text-blue-800 mt-0.5">{o.assigned_slot_snapshot.location}</p>
                                        )}
                                        {o.assigned_slot_snapshot.notes && (
                                            <p className="text-xs text-blue-700 mt-0.5 italic">{o.assigned_slot_snapshot.notes}</p>
                                        )}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </AccountSection>

            <div className="mt-12 bg-[#F2D8D5] rounded-2xl p-8 flex items-start gap-4" data-testid="account-donate-cta">
                <Heart className="w-6 h-6 text-[#E53935] flex-shrink-0 mt-1" fill="#E53935" strokeWidth={1.5} />
                <div>
                    <h3 className="font-heading text-xl text-[#1C252E] font-medium mb-1">Want to give back?</h3>
                    <p className="text-sm text-[#4A5568] mb-3">Make a one-time or monthly gift directly to our school community.</p>
                    <Link to="/donate" className="text-sm font-bold text-[#E53935] hover:underline">
                        Donate now →
                    </Link>
                </div>
            </div>

            {/* Recurring donations */}
            {donations.filter((d) => d.frequency === "monthly").length > 0 && (
                <AccountSection eyebrow="Recurring giving" title="Monthly giving" icon={Heart} testid="account-recurring-section">
                    <div className="space-y-3" data-testid="account-recurring-list">
                        {donations.filter((d) => d.frequency === "monthly").map((d) => (
                            <div key={d.id} className="bg-white border border-black/5 rounded-2xl p-5 flex items-center justify-between gap-4 flex-wrap" data-testid={`account-recurring-${d.id}`}>
                                <div className="flex-1 min-w-0">
                                    <p className="font-heading text-xl text-[#1C252E] font-medium">${d.amount.toFixed(2)}<span className="text-sm text-[#4A5568] font-normal"> / month</span></p>
                                    <p className="text-xs text-[#4A5568] mt-1">
                                        Started {new Date(d.created_at).toLocaleDateString()}
                                        {d.cancelled_at && ` · Cancelled ${new Date(d.cancelled_at).toLocaleDateString()}`}
                                    </p>
                                </div>
                                <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold capitalize ${d.status === "cancelled" ? "bg-gray-100 text-gray-600" : "bg-green-100 text-green-700"}`}>
                                    {d.status || "active"}
                                </span>
                                <a
                                    href={`${API}/donations/${d.id}/receipt.pdf?email=${encodeURIComponent(user.email)}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    data-testid={`account-receipt-${d.id}`}
                                    className="text-sm font-medium text-[#1C252E] hover:text-[#E53935] flex items-center gap-1"
                                >
                                    <FileText className="w-4 h-4" strokeWidth={1.5} />
                                    Receipt
                                </a>
                                {d.status !== "cancelled" && (
                                    <button
                                        onClick={() => cancelMonthly(d.id)}
                                        data-testid={`account-cancel-recurring-${d.id}`}
                                        className="text-sm font-medium text-[#E53935] hover:underline flex items-center gap-1"
                                    >
                                        <X className="w-4 h-4" strokeWidth={1.5} />
                                        Cancel
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                </AccountSection>
            )}

            {/* Phase 4 — Volunteer hours self-tracker */}
            <VolunteerHoursSelfPanel />

            {/* Phase 5 — Peer-to-peer organizer status */}
            <OrganizerCard />

            {/* 2FA — FIDO2 / WebAuthn security keys */}
            <SecurityKeysPanel />

            {/* Self-service account management */}
            <AccountSettingsPanel />
        </div>
    );
};

export default Account;
